/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {RefObject, useCallback, useEffect, useRef} from 'react';
import {QueryClient, QueryKey} from 'react-query';
import {warn} from '@ion/core/src/logger/logger';
import {JobProgressUpdate} from '@ion/services/src/websocket/types';
import {JobCacheItem, JobCacheTask, mergeIntoJob, reconcileWithReplay} from './JobCacheMerge';
import useFetchWindowBuffer from './useFetchWindowBuffer';
import {JobBlock} from './useJobGridLiveUpdates';
import useJobSubscriptions from './useJobSubscriptions';

export interface DevicesGridHandle {
  refreshInfiniteCache?: () => void
}

export interface JobTabLiveUpdatesProps {
  queryClient: QueryClient,
  jobKey: QueryKey,
  jobId: number | string,
  isFetching: boolean,
  selectedTask: number,
  devicesGridRef: RefObject<DevicesGridHandle>,
  invalidateDevices: () => void
}

export interface JobTabLiveUpdatesResult {
  onDevicesSettled: () => void
}

const REFRESH_RELEASE_MS = 10000;

const taskOf = (job: JobCacheItem | undefined, taskIndex: number): JobCacheTask | undefined => (
  job?.tasks?.find(task => task.taskIndex === taskIndex)
);

/**
 * Applies `jobProgress` changes to the Job Details tab. There is one write
 * rather than the grid's two: `JobSummary` and `TasksGrid` both render from the
 * `LOAD_JOB_ID` entry and neither is memoised, so writing that entry is the
 * repaint. The devices sub-grid below them is fed by its own query, and is
 * re-read in place only when the merge replaced the task it is showing.
 *
 * The tab knows its `jobId` before it fetches, so it subscribes on mount and is
 * already observing while `LOAD_JOB_ID` is in flight - which is what makes the
 * in-flight window load-bearing here rather than merely defensive: a change
 * merged during the round trip is clobbered by the response unless it is
 * replayed onto it.
 *
 * ```typescript
 *
 *   import useJobTabLiveUpdates from './useJobTabLiveUpdates';
 *
 *   const {onDevicesSettled} = useJobTabLiveUpdates({
 *     queryClient, jobKey, jobId, isFetching, selectedTask,
 *     devicesGridRef, invalidateDevices
 *   });
 *
 * ```
 *
 * @param   queryClient The client holding the `LOAD_JOB_ID` entry being patched.
 * @param   jobKey The key that entry is stored under, built once by the caller
 *          so this hook and `useJobId` cannot disagree about it.
 * @param   jobId This tab's job, which arrives as a string from the tab key.
 * @param   isFetching Whether `useJobId` has a request in flight - the edges of
 *          this are the tab's fetch seam, since it owns no promise to wrap.
 * @param   selectedTask The `taskIndex` the devices sub-grid is querying.
 * @param   devicesGridRef Ref to the devices grid's imperative handle.
 * @param   invalidateDevices Marks the cached device pages stale so the re-read
 *          reaches the network. It must invalidate rather than remove them -
 *          removing destroys the query, which cancels whatever request is in
 *          flight for it, and a cancellation is delivered to the grid as a
 *          zero-row response.
 * @returns `onDevicesSettled`, which the devices grid calls when a load has
 *          finished - the signal the one-at-a-time latch releases on.
 */
export const useJobTabLiveUpdates = ({
  queryClient,
  jobKey,
  jobId,
  isFetching,
  selectedTask,
  devicesGridRef,
  invalidateDevices
}: JobTabLiveUpdatesProps): JobTabLiveUpdatesResult => {
  const observedId = Number(jobId);

  const {openWindow, record, changesFor} = useFetchWindowBuffer();

  const preFetch    = useRef<JobBlock | undefined>(undefined);
  const closeWindow = useRef<(() => void) | null>(null);

  const refreshInFlight = useRef(false);
  const refreshPending  = useRef(false);
  const releaseTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settleRef       = useRef<() => void>(() => undefined);

  const startRefresh = useCallback((): void => {
    refreshInFlight.current = true;
    releaseTimer.current    = setTimeout(() => settleRef.current(), REFRESH_RELEASE_MS);
    invalidateDevices();
    devicesGridRef.current?.refreshInfiniteCache?.();
  }, [invalidateDevices, devicesGridRef, selectedTask]);

  const refreshDevices = useCallback((): void => {
    if (refreshInFlight.current) {
      refreshPending.current = true;
    } else {
      startRefresh();
    }
  }, [startRefresh]);

  /**
   * Released by the devices grid once a load has finished, whatever its
   * outcome. A delta which arrived while that load was running is run now, as
   * one re-read rather than as however many deltas landed.
   */
  const onDevicesSettled = useCallback((): void => {
    if (releaseTimer.current) {
      clearTimeout(releaseTimer.current);
      releaseTimer.current = null;
    }
    refreshInFlight.current = false;
    if (refreshPending.current) {
      refreshPending.current = false;
      startRefresh();
    }
  }, [startRefresh]);

  useEffect(() => {
    settleRef.current = onDevicesSettled;
  });

  useEffect(() => () => {
    if (releaseTimer.current) {
      clearTimeout(releaseTimer.current);
      releaseTimer.current = null;
    }
  }, []);

  const onDelta = useCallback((delta: JobProgressUpdate, id: number): void => {
    record(id, delta);

    const entry  = queryClient.getQueryData<JobBlock>(jobKey);
    const items  = entry?.items ? [...entry.items] : [];
    const index  = items.findIndex(item => Number(item.jobId) === id);
    const cached = index === -1 ? undefined : items[index];
    const {accepted, job, unplaced} = mergeIntoJob(cached, delta);

    if (unplaced?.length) {
      warn(`[ADM] jobProgress change for job ${id} addressed task index(es) ` +
        `[${unplaced.join(',')}] which the cached job does not hold, the task ` +
        'scope of the change was dropped');
    }
    if (accepted && job) {
      items[index] = job;
      queryClient.setQueryData(jobKey, {...entry, items});
      if (taskOf(job, selectedTask) !== taskOf(cached, selectedTask)) {
        refreshDevices();
      }
    }
  }, [queryClient, jobKey, record, selectedTask, refreshDevices]);

  const onResync = useCallback((): void => {
    queryClient.invalidateQueries(jobKey, {exact: true});
    refreshDevices();
  }, [queryClient, jobKey, refreshDevices]);

  const {observe} = useJobSubscriptions({onDelta, onResync});

  useEffect(() => {
    observe([observedId]);
  }, [observedId, observe]);

  /**
   * Reconciles the response against the entry as it stood before the request
   * went out, then replays the changes which arrived while it was in flight.
   *
   * Both halves are judged by `JobCacheMerge` and by nothing else: the first is
   * a snapshot comparison, the second is the same guard a live change is put
   * through, so a replay no longer newest for its scope is rejected exactly as
   * a late change would be.
   */
  const settleFetch = useCallback((): void => {
    const entry = queryClient.getQueryData<JobBlock>(jobKey);
    const items = entry?.items ? [...entry.items] : [];
    const index = items.findIndex(item => Number(item.jobId) === observedId);

    if (index !== -1) {
      const incoming     = items[index];
      const cached       = preFetch.current?.items?.find(item => Number(item.jobId) === observedId);
      const {job, moved} = reconcileWithReplay(cached, incoming, changesFor(observedId));

      if (moved) {
        items[index] = job;
        queryClient.setQueryData(jobKey, {...entry, items});
      }
    }
    preFetch.current = undefined;
  }, [queryClient, jobKey, observedId, changesFor]);

  useEffect(() => {
    if (isFetching && !closeWindow.current) {
      preFetch.current    = queryClient.getQueryData<JobBlock>(jobKey);
      closeWindow.current = openWindow();
    } else if (!isFetching && closeWindow.current) {
      settleFetch();
      closeWindow.current();
      closeWindow.current = null;
    }
  }, [isFetching, jobKey, queryClient, openWindow, settleFetch]);

  useEffect(() => () => {
    closeWindow.current?.();
    closeWindow.current = null;
  }, []);

  return {onDevicesSettled};
};

export default useJobTabLiveUpdates;
